import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import * as api from "../../../api/cabinet";
import { useCabinet } from "../../../context/CabinetContext";
import {
  getSkinsCatalog,
  skinByFolder,
  DEFAULT_FREE_SKIN,
  SKIN_PRICE,
  priceForSkin,
} from "../../../data/skinsCatalog";
import BlobCanvas from "../BlobCanvas";
import CabIcon from "../CabIcon";
import "../../../styles/cabinet-avatar.css";

import SkinErrorBoundary from "../SkinErrorBoundary";

// 3D-превью грузится отдельным чанком — three.js не входит в основной бандл
const AvatarPreview3D = lazy(() => import("../AvatarPreview3D"));
const OutfitCreator = lazy(() => import("./OutfitCreator"));

/* вкладки аватара — 1-в-1 из AvatarScreen.kt */
const AVATAR_TABS = [
  { id: "body", title: "Скины", ready: true },
  // Образы временно скрыты — переехали в каталог Скинов
  // { id: "outfits", title: "Образы", ready: true },
  { id: "clothing", title: "Одежда", ready: false },
  { id: "accessories", title: "Аксессуары", ready: false },
  { id: "animations", title: "Анимации", ready: false },
  { id: "emotes", title: "Эмоции", ready: false },
];

/**
 * AvatarScreen — экран «Аватар». Порт AvatarScreen.kt, 1-в-1.
 * hero с балансом → табы → превью выбранного скина + кнопка действия →
 * сетка скинов 2 колонки. 3D-превью заменено на крупный avatar.png.
 *
 * «equipped» (надетый скин) — selectedSkin из CabinetContext (localStorage).
 */
export default function AvatarScreen() {
  const {
    diamonds,
    setDiamondsLocal,
    selectedSkin,
    setSelectedSkin,
    refreshEconomy,
    userId,
  } = useCabinet();

  const [catalog, setCatalog] = useState([]);
  const [owned, setOwned] = useState(new Set([DEFAULT_FREE_SKIN]));
  const [selected, setSelected] = useState(selectedSkin); // что показано в превью
  const [activeTab, setActiveTab] = useState("body");
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState(null);
  const [promoCode, setPromoCode] = useState("");        // ввод промокода
  const [promoModal, setPromoModal] = useState(null);    // {name, price} — модалка успеха
  // Модалка подтверждения покупки. Открывается по кнопке «Купить».
  // {promoError} — заполняется, если введённый промокод оказался неверным
  // (тогда предлагаем купить за полную цену).
  const [confirmModal, setConfirmModal] = useState(null);

  /* каталог скинов + купленные */
  useEffect(() => {
    const ctrl = new AbortController();
    getSkinsCatalog(ctrl.signal)
      .then(setCatalog)
      // Массового preload всех glb здесь БОЛЬШЕ НЕТ (убран 2026-07-09):
      // 78 моделей × до 10МБ = ~275МБ разом забивали канал — превью
      // выбранного скина ждало в очереди ~15с, а прокси рвал лишние
      // HTTP/2-стримы (ERR_HTTP2_PROTOCOL_ERROR). Модель грузится
      // лениво через useGLTF при выборе скина — это 0.5-2с со спиннером.
      .catch(() => {});
    if (userId) {
      api
        .getOwnedSkins(userId, ctrl.signal)
        .then((folders) => {
          setOwned(new Set([...folders, DEFAULT_FREE_SKIN]));
        })
        .catch(() => {});
    }
    return () => ctrl.abort();
  }, [userId]);

  // Сортировка: сверху купленные/бесплатные (доступные юзеру),
  // снизу платные ещё-некупленные. Внутри каждой группы — порядок из CATALOG
  // (там бесплатные первыми, потом по алфавиту).
  const sortedCatalog = useMemo(() => {
    const isAvailable = (s) => s.price === 0 || owned.has(s.folder);
    const top = [];
    const bottom = [];
    for (const s of catalog) {
      if (isAvailable(s)) top.push(s);
      else bottom.push(s);
    }
    return [...top, ...bottom];
  }, [catalog, owned]);

  const selectedEntry =
    skinByFolder(catalog, selected) || catalog[0] || null;
  // Цена выбранного скина: 0 для дефолтных y-bot/x-bot, иначе из каталога.
  const selectedPrice = selectedEntry
    ? (Number.isFinite(selectedEntry.price) ? selectedEntry.price : priceForSkin(selectedEntry.folder))
    : SKIN_PRICE;
  const isFree = selectedPrice === 0;
  const isOwned = isFree || owned.has(selected);
  const isEquipped = selected === selectedSkin;
  const canAfford = (diamonds || 0) >= selectedPrice;
  // Введён ли промокод — тогда покупка возможна даже без полной суммы
  // (одноразовый промокод: любой скин за 1 алмаз, проверяется на бэке).
  const hasPromo = promoCode.trim().length > 0;

  /* надеть скин — локально (в CabinetContext + localStorage) */
  const handleEquip = () => {
    if (selectedEntry) setSelectedSkin(selectedEntry.folder);
  };

  /* Клик «Купить» — открываем модалку подтверждения (не покупаем сразу). */
  const handleBuy = () => {
    if (!selectedEntry || !userId) return;
    if (isFree) return; // бесплатные скины покупать не нужно — просто equip
    setError(null);
    setConfirmModal({ promoError: null });
  };

  /* Подтверждение из модалки. Промокод (если введён) проверяется на бэке —
   * его условие само определяет цену (1 алмаз, бесплатно и т.п.). Без
   * промокода — обычная покупка за полную цену. */
  const confirmPurchase = async (forceNoPromo = false) => {
    if (!selectedEntry || !userId) return;
    setPurchasing(true);
    // forceNoPromo — покупка за полную цену после неверного промокода
    // (setPromoCode async, в этом тике значение ещё старое — берём явно "").
    const code = forceNoPromo ? "" : promoCode.trim();
    try {
      const res = await api.purchaseSkin(
        userId,
        selectedEntry.folder,
        selectedPrice,
        code // пусто → обычная покупка; введён → проверяется на бэке
      );
      const remaining = res?.remaining_diamonds;
      if (remaining != null) setDiamondsLocal(remaining);
      refreshEconomy();
      setOwned((prev) => new Set([...prev, selectedEntry.folder]));
      setPromoCode("");
      setConfirmModal(null);
      // Успех: показываем модалку с реальной ценой (её вернул бэк по условию
      // промокода — 0/1/со скидкой), либо полную цену при обычной покупке.
      setPromoModal({
        name: selectedEntry.name,
        price: res?.price_paid ?? (res?.promo_applied ? 1 : selectedPrice),
        promo: !!res?.promo_applied,
      });
    } catch (e) {
      // ApiError.code = сырой error-код с бэка (надёжнее текста).
      const code = e?.code || "";
      const msg = e?.message || "Ошибка покупки";
      if (code === "promo_invalid") {
        // Промокод неверный — остаёмся в модалке, предлагаем купить за
        // полную цену (кнопка переключится на «Купить за полную цену»).
        setConfirmModal({ promoError: "Введённый промокод недействителен" });
      } else if (code === "promo_used") {
        setConfirmModal({ promoError: "Вы уже использовали этот промокод" });
      } else if (code === "not_enough_diamonds") {
        setConfirmModal({ promoError: "Недостаточно алмазов" });
      } else if (code === "already_owned") {
        // Уже куплен (гонка/двойной клик) — просто отмечаем владение.
        setOwned((prev) => new Set([...prev, selectedEntry.folder]));
        setConfirmModal(null);
      } else {
        setConfirmModal(null);
        setError(msg);
      }
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="rb-cab-screen rb-av">
      {/* ── hero с балансом ────────────────────────────────────── */}
      <div className="rb-av__hero">
        <BlobCanvas className="rb-av__hero-canvas" />
        <div className="rb-av__hero-inner">
          <div className="rb-av__hero-text">
            <h1 className="rb-av__hero-title">
              <CabIcon name="user" size={24} /> Аватар
            </h1>
            <p className="rb-av__hero-sub">Подбери свой стиль для всех игр</p>
          </div>
          <div className="rb-av__hero-diamonds">
            <CabIcon name="diamond" size={16} />
            {diamonds == null ? "..." : diamonds}
          </div>
        </div>
      </div>

      {/* ── табы ───────────────────────────────────────────────── */}
      <div className="rb-av__tabs">
        {AVATAR_TABS.map((t) => (
          <button
            key={t.id}
            className={`rb-cab-chip ${activeTab === t.id ? "is-active" : ""}`}
            disabled={!t.ready}
            onClick={() => t.ready && setActiveTab(t.id)}
          >
            {t.ready ? t.title : `${t.title} · скоро`}
          </button>
        ))}
      </div>

      {/* ── контент ────────────────────────────────────────────── */}
      {activeTab === "body" ? (
        <div className="rb-av__body">
          {/* превью */}
          {selectedEntry && (
            <div className="rb-av__preview">
              <div className="rb-av__preview-head">
                <span className="rb-av__preview-label">ПРЕВЬЮ</span>
                {isEquipped && (
                  <span className="rb-av__preview-equipped">
                    <CabIcon name="check" size={11} /> НАДЕТ
                  </span>
                )}
              </div>
              <div className="rb-av__preview-img">
                {selectedEntry.glb ? (
                  /* Граница обязательна: если glb не загрузился (сервер отдал
                     HTML-фолбэк, 404, обрыв), ошибка из useGLTF без неё сносит
                     весь кабинет вместе с WebGL-контекстом — тёмный экран.
                     При сбое молча остаёмся на статичной картинке скина. */
                  <SkinErrorBoundary
                    resetKey={selectedEntry.folder}
                    fallback={
                      <img
                        src={selectedEntry.avatar}
                        alt={selectedEntry.name}
                        className="rb-av__preview-img-fallback"
                      />
                    }
                  >
                    <Suspense
                      fallback={
                        <img
                          src={selectedEntry.avatar}
                          alt={selectedEntry.name}
                          className="rb-av__preview-img-fallback"
                        />
                      }
                    >
                      <AvatarPreview3D
                        glbUrl={selectedEntry.glb}
                        name={selectedEntry.name}
                      />
                    </Suspense>
                  </SkinErrorBoundary>
                ) : (
                  <img src={selectedEntry.avatar} alt={selectedEntry.name} />
                )}
              </div>
              <div className="rb-av__preview-name">{selectedEntry.name}</div>

              {/* кнопка действия */}
              {isOwned && isEquipped ? (
                <div className="rb-av__action rb-av__action--info rb-av__action--green">
                  <CabIcon name="check" size={16} /> Сейчас надет
                </div>
              ) : isOwned ? (
                <button
                  className="rb-av__action rb-av__action--accent"
                  onClick={handleEquip}
                  disabled={purchasing}
                >
                  Надеть
                </button>
              ) : (
                // Скин не куплен — кнопка «Купить» (слева) + инпут промокода
                // (справа). Кнопка нейтральная: реальную цену/условие
                // определяет бэк по промокоду (за алмазы, со скидкой,
                // бесплатно — промокоды бывают разные). Подтверждение и все
                // исходы (успех / неверный код / нехватка) — в модалке.
                <>
                  <div className="rb-av__buyrow">
                    <button
                      className="rb-av__action rb-av__action--accent rb-av__buybtn"
                      onClick={handleBuy}
                      disabled={purchasing}
                    >
                      Купить
                    </button>
                    <input
                      className="rb-av__promo-input"
                      type="text"
                      placeholder="Промокод"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      disabled={purchasing}
                      maxLength={32}
                      autoCapitalize="characters"
                      spellCheck={false}
                    />
                  </div>
                  <div className="rb-av__promo-hint">
                    Есть промокод? Введи его — иначе просто нажми «Купить».
                  </div>
                </>
              )}

              {error && (
                <div className="rb-av__error">
                  <CabIcon name="warning" size={14} /> {error}
                </div>
              )}
            </div>
          )}

          {/* сетка скинов */}
          <div className="rb-av__grid-wrap">
            <div className="rb-av__grid-label">ВЫБЕРИ СКИН</div>
            <div className="rb-av__grid">
              {sortedCatalog.map((skin) => {
                const sSelected = skin.folder === selected;
                const sEquipped = skin.folder === selectedSkin;
                const sOwned = owned.has(skin.folder);
                return (
                  <button
                    key={skin.folder}
                    className={`rb-av__card ${sSelected ? "is-selected" : ""}`}
                    onClick={() => setSelected(skin.folder)}
                  >
                    <div className="rb-av__card-img">
                      {/* Статичная PNG-тумбнейл скина — сгенерирована
                       * headless-рендером (scripts/generate_thumbnails.js).
                       * Живое 3D с анимацией только в большом превью слева. */}
                      <img
                        src={skin.avatar}
                        alt={skin.name}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                      {sEquipped && (
                        <span className="rb-av__card-equipped">
                          <CabIcon name="check" size={11} />
                        </span>
                      )}
                    </div>
                    <div className="rb-av__card-name">{skin.name}</div>
                    <div className="rb-av__card-price">
                      {skin.price === 0 ? (
                        <span className="rb-av__card-owned">
                          Бесплатно
                        </span>
                      ) : sOwned ? (
                        <span className="rb-av__card-owned">
                          <CabIcon name="check" size={12} /> Куплен
                        </span>
                      ) : (
                        <span>
                          <CabIcon name="diamond" size={12} /> {skin.price ?? SKIN_PRICE}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : activeTab === "outfits" ? (
        <Suspense
          fallback={
            <div className="rb-av__soon">
              <div className="rb-av__soon-text">Загрузка редактора…</div>
            </div>
          }
        >
          <OutfitCreator />
        </Suspense>
      ) : (
        <div className="rb-av__soon">
          <div className="rb-av__soon-emoji">
            <CabIcon name="construction" size={48} />
          </div>
          <div className="rb-av__soon-title">
            {AVATAR_TABS.find((t) => t.id === activeTab)?.title} · скоро
          </div>
          <div className="rb-av__soon-text">
            Эта вкладка появится в одном из следующих обновлений.
          </div>
        </div>
      )}

      {/* ── модалка подтверждения покупки ───────────────────────── */}
      {confirmModal && selectedEntry && (
        <div
          className="rb-av__promo-overlay"
          onClick={() => !purchasing && setConfirmModal(null)}
        >
          <div
            className="rb-av__promo-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-av__promo-modal-title">
              Покупка скина «{selectedEntry.name}»
            </div>

            {confirmModal.promoError ? (
              // Промокод ввели, но он не сработал — сообщаем причину и
              // предлагаем купить за полную цену (уже без кода).
              <div className="rb-av__promo-modal-text rb-av__promo-modal-text--warn">
                <CabIcon name="warning" size={15} /> {confirmModal.promoError}.
                {canAfford
                  ? " Купить за полную цену?"
                  : " Для покупки за полную цену не хватает алмазов."}
              </div>
            ) : hasPromo ? (
              <div className="rb-av__promo-modal-text">
                Применить промокод{" "}
                <b>«{promoCode.trim()}»</b> к этому скину?
              </div>
            ) : (
              <div className="rb-av__promo-modal-text">
                Купить за <CabIcon name="diamond" size={15} /> {selectedPrice}?
                {!canAfford && (
                  <div className="rb-av__promo-modal-text--warn" style={{ marginTop: 6 }}>
                    <CabIcon name="warning" size={14} /> Не хватает алмазов.
                  </div>
                )}
              </div>
            )}

            <div className="rb-av__buyrow" style={{ marginTop: 14 }}>
              <button
                className="rb-av__action rb-av__action--accent rb-av__buybtn"
                onClick={() => {
                  if (confirmModal.promoError) {
                    // Промокод не сработал — покупаем за полную цену без него.
                    setPromoCode("");
                    confirmPurchase(true);
                  } else {
                    confirmPurchase(false);
                  }
                }}
                disabled={
                  purchasing ||
                  // без промокода и без денег — купить нельзя
                  (!hasPromo && !canAfford) ||
                  // промокод не сработал и на полную цену не хватает
                  (!!confirmModal.promoError && !canAfford)
                }
              >
                {purchasing ? "Покупка…" : confirmModal.promoError ? "Купить за полную цену" : "Купить"}
              </button>
              <button
                className="rb-av__action rb-av__action--info"
                onClick={() => setConfirmModal(null)}
                disabled={purchasing}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── модалка «покупка удалась» ────────────────────────────── */}
      {promoModal && (
        <div
          className="rb-av__promo-overlay"
          onClick={() => setPromoModal(null)}
        >
          <div
            className="rb-av__promo-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-av__promo-modal-icon">
              <CabIcon name="check" size={40} />
            </div>
            <div className="rb-av__promo-modal-title">
              {promoModal.promo ? "Промокод сработал!" : "Скин куплен!"}
            </div>
            <div className="rb-av__promo-modal-text">
              {promoModal.price === 0 ? (
                <>Вы получили скин «{promoModal.name}» бесплатно</>
              ) : (
                <>
                  Вы получили скин «{promoModal.name}» за{" "}
                  <CabIcon name="diamond" size={15} /> {promoModal.price}
                </>
              )}
            </div>
            <button
              className="rb-av__action rb-av__action--accent"
              onClick={() => setPromoModal(null)}
            >
              Отлично
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
